import { getApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ensureJsAuthFromNative } from './authBridge';
import type {
  Tournament,
  TournamentParticipant,
  TournamentSubmission,
  TournamentLeaderboardEntry,
} from '../types';

const app = getApp();
const functions = getFunctions(app, 'us-central1');
const auth = getAuth(app);

const tournamentMutationFlights = new Map<string, Promise<unknown>>();

function runTournamentMutationOnce<T>(key: string, task: () => Promise<T>): Promise<T> {
  const existing = tournamentMutationFlights.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = task().finally(() => {
    if (tournamentMutationFlights.get(key) === promise) {
      tournamentMutationFlights.delete(key);
    }
  });

  tournamentMutationFlights.set(key, promise);
  return promise;
}

const mutationUid = () => auth.currentUser?.uid || 'anon';
const stablePayloadKey = (value: unknown) => {
  try {
    return JSON.stringify(value) || '';
  } catch {
    return String(value ?? '');
  }
};

const call = <Request, Response = any>(name: string) =>
  httpsCallable<Request, Response>(functions, name);

const createDraftCallable = call<Partial<Tournament> & { name: string }, { ok: true; tournamentId: string }>('tournamentCreateDraft');
const checkNameCallable = call<
  { name: string; tournamentId?: string | null },
  { ok: true; available: boolean; normalizedName: string }
>('tournamentCheckNameAvailability');
const updateDraftCallable = call<{ tournamentId: string; patch: Partial<Tournament> }, { ok: true }>('tournamentUpdateDraft');
const submitCallable = call<{ tournamentId: string }, { ok: true }>('tournamentSubmitForApproval');
const reviewCallable = call<{ tournamentId: string; action: 'approve' | 'changes_requested' | 'reject'; notes?: string }, { ok: true; status: string; approvalStatus: string }>('adminReviewTournament');
const lifecycleCallable = call<{ tournamentId: string; action: 'reopen_for_changes' | 'cancel' | 'archive'; notes?: string }, { ok: true; status: string; approvalStatus: string }>('adminTournamentLifecycle');
const registrationCallable = call<{ tournamentId: string; action: 'open' | 'close' }, { ok: true; status: string }>('adminSetTournamentRegistration');
const joinCallable = call<{ tournamentId: string; displayName: string; avatarUrl?: string | null; teamName?: string; duoAction?: 'create' | 'join'; duoCode?: string }, { ok: true; status: 'pending' | 'approved'; teamId?: string | null; teamName?: string | null; duoCode?: string | null; duoComplete?: boolean }>('tournamentJoinRegistration');
const getMyDuoCallable = call<{ tournamentId: string }, { ok: true; teamId: string; teamName: string; captainUid?: string | null; memberUids: string[]; status: string; duoCode: string; complete: boolean }>('tournamentGetMyDuo');
const leaveCallable = call<{ tournamentId: string }, { ok: true }>('tournamentLeaveRegistration');
const manageParticipantCallable = call<{ tournamentId: string; participantUid: string; action: 'approve' | 'remove'; reason?: string }, { ok: true }>('adminManageTournamentParticipant');

const submissionStateCallable = call<
  { tournamentId: string; action: 'open' | 'close' },
  { ok: true; status: string; tournamentCode?: string | null }
>('adminSetTournamentSubmissionState');

const createSubmissionCallable = call<
  {
    tournamentId: string;
    catchId: string;
    measurementValue?: number | null;
    measurementUnit?: 'in' | 'cm' | 'lb' | 'kg' | 'points' | null;
  },
  { ok: true; submissionId: string; status: 'submitted' }
>('tournamentCreateSubmission');

const withdrawSubmissionCallable = call<
  { tournamentId: string; submissionId: string },
  { ok: true }
>('tournamentWithdrawSubmission');

const reviewSubmissionCallable = call<
  {
    tournamentId: string;
    submissionId: string;
    action: 'accept' | 'reject';
    notes?: string;
  },
  { ok: true; status: 'accepted' | 'rejected' }
>('tournamentReviewSubmission');

const calculateStandingsCallable = call<
  { tournamentId: string },
  { ok: true; status: 'provisional_results'; leaderboardCount: number }
>('tournamentCalculateStandings');

const finalizeResultsCallable = call<
  { tournamentId: string },
  { ok: true; status: 'finalized'; leaderboardCount: number; winnerCount: number }
>('tournamentFinalizeResults');

const resolveParticipantDisputeCallable = call<
  {
    tournamentId: string;
    participantUid: string;
    action: 'disqualify' | 'reinstate';
    reason: string;
  },
  {
    ok: true;
    status: 'provisional_results';
    participantStatus: 'disqualified' | 'approved';
    leaderboardCount: number;
  }
>('tournamentResolveParticipantDispute');

const repairResultsCallable = call<
  { tournamentId: string },
  {
    ok: true;
    status: string;
    leaderboardCount: number;
    winnerCount: number;
  }
>('adminRepairTournamentResults');

const settlePayoutsCallable = call<
  { tournamentId: string },
  {
    ok: true;
    alreadySettled?: boolean;
    payoutStatus: 'paid' | 'refunded' | 'not_applicable';
    paidShells: number;
    recipientCount?: number;
    recipients?: Array<{
      userId: string;
      userName: string;
      placement: number;
      shellsAwarded: number;
      competitorId: string;
    }>;
    refundSummary?: {
      refundedEntries: number;
      refundedShells: number;
    };
  }
>('tournamentSettlePayouts');

export function subscribeToTournaments(
  onUpdate: (tournaments: Record<string, Tournament>) => void,
  options?: { includeAdminAll?: boolean },
): () => void {
  const PUBLIC_CURRENT_STATUSES = [
    'approved',
    'registration_open',
    'active',
    'live',
    'submissions_closed',
    'under_review',
    'provisional_results',
  ] as const;

  let cancelled = false;
  let authUnsubscribe: (() => void) | null = null;
  let firestoreUnsubscribes: Array<() => void> = [];
  let bootInFlight = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let permissionRetryCount = 0;

  const buckets = new Map<string, Record<string, Tournament>>();

  const clearRetryTimer = () => {
    if (!retryTimer) return;
    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const clearFirestoreListeners = () => {
    firestoreUnsubscribes.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch {}
    });
    firestoreUnsubscribes = [];
    buckets.clear();
  };

  const emitMerged = () => {
    if (cancelled) return;
    const merged: Record<string, Tournament> = {};
    buckets.forEach((bucket) => {
      Object.assign(merged, bucket);
    });
    onUpdate(merged);
  };

  const snapshotToMap = (snapshot: any) => {
    const next: Record<string, Tournament> = {};
    snapshot.forEach((item: any) => {
      next[item.id] = { ...(item.data() as Tournament), id: item.id };
    });
    return next;
  };

  const scheduleRetry = () => {
    if (
      cancelled ||
      permissionRetryCount >= 1 ||
      !auth.currentUser?.uid ||
      retryTimer
    ) {
      return;
    }

    permissionRetryCount += 1;
    clearFirestoreListeners();

    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (cancelled) return;

      void ensureJsAuthFromNative('tournaments-permission-retry')
        .catch(() => false)
        .finally(() => {
          if (!cancelled) attach();
        });
    }, 750);
  };

  const attachQuery = (
    key: string,
    q: ReturnType<typeof query>,
    options?: { toleratePermissionDenied?: boolean },
  ) => {
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        permissionRetryCount = 0;
        buckets.set(key, snapshotToMap(snapshot));
        emitMerged();
      },
      (error: any) => {
        buckets.delete(key);
        emitMerged();

        if (error?.code === 'permission-denied') {
          // The broad listener is intentionally best-effort: admins can read it,
          // normal users cannot. User-facing tournament data comes from the
          // rules-compatible "mine" and public lifecycle listeners below.
          if (options?.toleratePermissionDenied) return;

          // Native auth can briefly attach before the JS token bridge settles.
          scheduleRetry();
          return;
        }

        console.error(`[tournaments] ${key} subscription failed`, error);
      },
    );

    firestoreUnsubscribes.push(unsubscribe);
  };

  const attach = () => {
    const uid = auth.currentUser?.uid;
    if (cancelled || firestoreUnsubscribes.length || !uid) return;

    const tournamentsRef = collection(db, 'tournaments');

    // Admin moderation needs the complete tournament feed, but normal users
    // must never attach this broad collection listener. Previously every user
    // opened it and Firestore intentionally denied it, which still produced
    // noisy Listen/channel transport errors in the browser console.
    if (options?.includeAdminAll === true) {
      attachQuery(
        'adminAll',
        query(tournamentsRef, orderBy('startDate', 'asc')),
      );
    }

    // Directors must always see every tournament they own, including drafts,
    // pending review, changes requested, rejected, and private/crew events.
    attachQuery(
      'mine',
      query(tournamentsRef, where('directorUid', '==', uid)),
    );

    // Firestore rules cannot authorize an unfiltered collection scan for normal
    // users. Query the exact public lifecycle states the rules permit instead.
    attachQuery(
      'publicCurrent',
      query(
        tournamentsRef,
        where('visibility', '==', 'public'),
        where('status', 'in', [...PUBLIC_CURRENT_STATUSES]),
      ),
    );

  };

  const boot = async () => {
    if (cancelled || bootInFlight || firestoreUnsubscribes.length) return;
    bootInFlight = true;

    try {
      const isNative = (() => {
        try {
          return Capacitor.isNativePlatform();
        } catch {
          return false;
        }
      })();

      if (isNative) {
        try {
          await ensureJsAuthFromNative('tournaments-subscribe');
        } catch (error) {
          console.warn('[tournaments] auth bridge wait failed', error);
        }
      }

      const startedAt = Date.now();
      while (!cancelled && !auth.currentUser?.uid && Date.now() - startedAt < 3000) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      if (!cancelled && auth.currentUser?.uid) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attach();
      }
    } finally {
      bootInFlight = false;
    }
  };

  authUnsubscribe = onAuthStateChanged(auth, (user) => {
    if (cancelled) return;

    clearRetryTimer();
    clearFirestoreListeners();
    permissionRetryCount = 0;

    if (!user) {
      onUpdate({});
      return;
    }

    void boot();
  });

  void boot();

  return () => {
    cancelled = true;
    clearRetryTimer();
    clearFirestoreListeners();

    if (authUnsubscribe) {
      authUnsubscribe();
      authUnsubscribe = null;
    }
  };
}


export const PUBLIC_TOURNAMENT_HISTORY_STATUSES = [
  'finalized',
  'completed',
  'rejected',
  'cancelled',
  'archived',
] as const;

/**
 * Cursor-friendly public history query used by Tournament Dock's Past tab.
 * Terminal events do not need a permanent realtime listener.
 */
export function getPublicTournamentHistoryQuery() {
  return query(
    collection(db, 'tournaments'),
    where('visibility', '==', 'public'),
    where('status', 'in', [...PUBLIC_TOURNAMENT_HISTORY_STATUSES]),
    orderBy('startDate', 'desc'),
  );
}

/** One-shot lookup keeps direct links to historical tournaments working. */
export async function getTournamentById(tournamentId: string): Promise<Tournament | null> {
  const id = String(tournamentId || '').trim();
  if (!id) return null;
  const snapshot = await getDoc(doc(db, 'tournaments', id));
  return snapshot.exists()
    ? ({ ...(snapshot.data() as Tournament), id: snapshot.id } as Tournament)
    : null;
}

export function subscribeTournamentParticipants(
  tournamentId: string,
  onUpdate: (participants: Record<string, TournamentParticipant>) => void,
): () => void {
  const q = query(
    collection(db, 'tournaments', tournamentId, 'participants'),
    orderBy('joinedAt', 'asc'),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const next: Record<string, TournamentParticipant> = {};
      snapshot.forEach((item) => {
        next[item.id] = {
          ...(item.data() as TournamentParticipant),
          id: item.id,
        };
      });
      onUpdate(next);
    },
    (error) => console.error('[tournament participants] subscription failed', error),
  );
}

export function subscribeTournamentParticipant(
  tournamentId: string,
  uid: string,
  onUpdate: (participant: TournamentParticipant | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'tournaments', tournamentId, 'participants', uid),
    (snapshot) => {
      onUpdate(
        snapshot.exists()
          ? ({ ...(snapshot.data() as TournamentParticipant), id: snapshot.id } as TournamentParticipant)
          : null,
      );
    },
    (error) => console.error('[tournament participant] subscription failed', error),
  );
}

export type MyTournamentParticipationState = Record<string, TournamentParticipant | null>;

/**
 * Lightweight map-facing participant state. We intentionally watch only the
 * current tournament ids supplied by the caller instead of collection-scanning
 * participant subcollections. This keeps Big Blue aware of JOINED/PENDING
 * lifecycle state without weakening Firestore rules or copying roster data onto
 * public tournament documents.
 */
export function subscribeMyTournamentParticipationStates(
  tournamentIds: string[],
  uid: string,
  onUpdate: (state: MyTournamentParticipationState) => void,
): () => void {
  const cleanUid = String(uid || '').trim();
  const ids = Array.from(new Set((tournamentIds || []).map((id) => String(id || '').trim()).filter(Boolean))).slice(0, 80);
  if (!cleanUid || !ids.length) {
    onUpdate({});
    return () => {};
  }

  let cancelled = false;
  const state: MyTournamentParticipationState = {};
  const emit = () => {
    if (!cancelled) onUpdate({ ...state });
  };

  const unsubs = ids.map((tournamentId) =>
    onSnapshot(
      doc(db, 'tournaments', tournamentId, 'participants', cleanUid),
      (snapshot) => {
        state[tournamentId] = snapshot.exists()
          ? ({ ...(snapshot.data() as TournamentParticipant), id: snapshot.id } as TournamentParticipant)
          : null;
        emit();
      },
      (error) => {
        // A denied/private event should not break the rest of the map lifecycle.
        state[tournamentId] = null;
        emit();
        if (error?.code !== 'permission-denied') {
          console.error('[tournament participant map state] subscription failed', tournamentId, error);
        }
      },
    ),
  );

  return () => {
    cancelled = true;
    unsubs.forEach((unsubscribe) => {
      try { unsubscribe(); } catch {}
    });
  };
}

const resultsSeenKey = (uid: string) => `roddo.tournament.resultsSeen.v1.${String(uid || 'guest').trim() || 'guest'}`;

export function hasSeenTournamentResults(uid: string, tournamentId: string): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const raw = window.localStorage.getItem(resultsSeenKey(uid));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && Number(parsed[String(tournamentId || '')] || 0) > 0;
  } catch {
    return false;
  }
}

export function markTournamentResultsSeen(uid: string, tournamentId: string): void {
  try {
    if (typeof window === 'undefined') return;
    const id = String(tournamentId || '').trim();
    if (!id) return;
    const key = resultsSeenKey(uid);
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === 'object' ? parsed : {};
    next[id] = Date.now();
    const trimmed = Object.fromEntries(
      Object.entries(next)
        .sort((a: any, b: any) => Number(b[1] || 0) - Number(a[1] || 0))
        .slice(0, 120),
    );
    window.localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {}
}

export function subscribeMyTournamentSubmissions(
  tournamentId: string,
  uid: string,
  onUpdate: (submissions: TournamentSubmission[]) => void,
): () => void {
  const q = query(
    collection(db, 'tournaments', tournamentId, 'submissions'),
    where('participantUid', '==', uid),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({
          ...(item.data() as TournamentSubmission),
          id: item.id,
        }))
        .sort((a, b) => Number(b.submittedAt || 0) - Number(a.submittedAt || 0));
      onUpdate(rows);
    },
    (error) => console.error('[my tournament submissions] subscription failed', error),
  );
}

export function subscribeTournamentAcceptedSubmissions(
  tournamentId: string,
  onUpdate: (submissions: TournamentSubmission[]) => void,
): () => void {
  const q = query(
    collection(db, 'tournaments', tournamentId, 'submissions'),
    where('status', '==', 'accepted'),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs
        .map((item) => ({
          ...(item.data() as TournamentSubmission),
          id: item.id,
        }))
        .sort(
          (a, b) =>
            Number(b.submittedAt || 0) - Number(a.submittedAt || 0),
        );

      onUpdate(rows);
    },
    (error) =>
      console.error(
        '[accepted tournament submissions] subscription failed',
        error,
      ),
  );
}

export function subscribeTournamentSubmissionsForReview(
  tournamentId: string,
  onUpdate: (submissions: TournamentSubmission[]) => void,
): () => void {
  const q = query(
    collection(db, 'tournaments', tournamentId, 'submissions'),
    orderBy('submittedAt', 'desc'),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      onUpdate(
        snapshot.docs.map((item) => ({
          ...(item.data() as TournamentSubmission),
          id: item.id,
        })),
      );
    },
    (error) => console.error('[tournament submission review] subscription failed', error),
  );
}

export function subscribeTournamentLeaderboard(
  tournamentId: string,
  onUpdate: (rows: TournamentLeaderboardEntry[]) => void,
): () => void {
  const q = query(
    collection(db, 'tournaments', tournamentId, 'leaderboard'),
    orderBy('placement', 'asc'),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      onUpdate(
        snapshot.docs
          .map((item) => ({
            ...(item.data() as TournamentLeaderboardEntry),
            id: item.id,
          }))
          .sort((a, b) => {
            if (a.placement !== b.placement) return a.placement - b.placement;
            if (b.score !== a.score) return b.score - a.score;
            const aName = a.teamName || a.participantName || a.competitorId;
            const bName = b.teamName || b.participantName || b.competitorId;
            return aName.localeCompare(bName);
          }),
      );
    },
    (error) => console.error('[tournament leaderboard] subscription failed', error),
  );
}

export async function checkTournamentNameAvailability(
  name: string,
  tournamentId?: string | null,
) {
  const cleanName = String(name || '').trim();
  if (cleanName.length < 3) {
    return { available: false, normalizedName: cleanName.toLowerCase() };
  }

  const result = await checkNameCallable({
    name: cleanName,
    tournamentId: tournamentId || null,
  });

  return result.data;
}

export async function createTournamentDraft(data: Partial<Tournament> & { name: string }) {
  return runTournamentMutationOnce(
    `draft:create:${mutationUid()}:${stablePayloadKey(data)}`,
    async () => {
      const result = await createDraftCallable(data);
      return result.data.tournamentId;
    },
  );
}

export async function updateTournamentDraft(tournamentId: string, patch: Partial<Tournament>) {
  await runTournamentMutationOnce(
    `draft:update:${tournamentId}:${stablePayloadKey(patch)}`,
    async () => {
      await updateDraftCallable({ tournamentId, patch });
    },
  );
}

export async function submitTournamentForApproval(tournamentId: string) {
  await runTournamentMutationOnce(`submit-for-approval:${tournamentId}`, async () => {
    await submitCallable({ tournamentId });
  });
}

export async function adminReviewTournament(
  tournamentId: string,
  action: 'approve' | 'changes_requested' | 'reject',
  notes = '',
) {
  return runTournamentMutationOnce(
    `admin-review:${tournamentId}:${action}:${stablePayloadKey(notes)}`,
    async () => {
      const result = await reviewCallable({ tournamentId, action, notes });
      return result.data;
    },
  );
}

export async function adminTournamentLifecycle(
  tournamentId: string,
  action: 'reopen_for_changes' | 'cancel' | 'archive',
  notes = '',
) {
  return runTournamentMutationOnce(
    `lifecycle:${tournamentId}:${action}:${stablePayloadKey(notes)}`,
    async () => {
      const result = await lifecycleCallable({ tournamentId, action, notes });
      return result.data;
    },
  );
}

export async function adminSetTournamentRegistration(
  tournamentId: string,
  action: 'open' | 'close',
) {
  return runTournamentMutationOnce(`registration:${tournamentId}:${action}`, async () => {
    const result = await registrationCallable({ tournamentId, action });
    return result.data;
  });
}

export async function joinTournamentRegistration(
  tournamentId: string,
  displayName: string,
  avatarUrl?: string | null,
) {
  return runTournamentMutationOnce(
    `join:${tournamentId}:${mutationUid()}`,
    async () => {
      const result = await joinCallable({ tournamentId, displayName, avatarUrl });
      return result.data.status;
    },
  );
}

export async function createTournamentDuoRegistration(
  tournamentId: string,
  displayName: string,
  avatarUrl: string | null | undefined,
  teamName: string,
) {
  return runTournamentMutationOnce(
    `duo:create:${tournamentId}:${mutationUid()}`,
    async () => {
      const result = await joinCallable({
        tournamentId,
        displayName,
        avatarUrl,
        teamName,
        duoAction: 'create',
      });
      return result.data;
    },
  );
}

export async function joinTournamentDuoRegistration(
  tournamentId: string,
  displayName: string,
  avatarUrl: string | null | undefined,
  duoCode: string,
) {
  return runTournamentMutationOnce(
    `duo:join:${tournamentId}:${mutationUid()}:${String(duoCode || '').trim().toUpperCase()}`,
    async () => {
      const result = await joinCallable({
        tournamentId,
        displayName,
        avatarUrl,
        duoAction: 'join',
        duoCode,
      });
      return result.data;
    },
  );
}

export async function getMyTournamentDuo(tournamentId: string) {
  const result = await getMyDuoCallable({ tournamentId });
  return result.data;
}

export async function leaveTournamentRegistration(tournamentId: string) {
  await runTournamentMutationOnce(`leave:${tournamentId}:${mutationUid()}`, async () => {
    await leaveCallable({ tournamentId });
  });
}

export async function adminManageTournamentParticipant(
  tournamentId: string,
  participantUid: string,
  action: 'approve' | 'remove',
  reason = '',
) {
  await runTournamentMutationOnce(
    `participant:${tournamentId}:${participantUid}:${action}:${stablePayloadKey(reason)}`,
    async () => {
      await manageParticipantCallable({ tournamentId, participantUid, action, reason });
    },
  );
}

export async function adminSetTournamentSubmissionState(
  tournamentId: string,
  action: 'open' | 'close',
) {
  return runTournamentMutationOnce(`submission-state:${tournamentId}:${action}`, async () => {
    const result = await submissionStateCallable({ tournamentId, action });
    return result.data;
  });
}

export async function createTournamentSubmission(args: {
  tournamentId: string;
  catchId: string;
  measurementValue?: number | null;
  measurementUnit?: 'in' | 'cm' | 'lb' | 'kg' | 'points' | null;
}) {
  return runTournamentMutationOnce(
    `submission:create:${args.tournamentId}:${args.catchId}`,
    async () => {
      const result = await createSubmissionCallable(args);
      return result.data;
    },
  );
}

export async function withdrawTournamentSubmission(
  tournamentId: string,
  submissionId: string,
) {
  await runTournamentMutationOnce(`submission:withdraw:${tournamentId}:${submissionId}`, async () => {
    await withdrawSubmissionCallable({ tournamentId, submissionId });
  });
}

export async function reviewTournamentSubmission(
  tournamentId: string,
  submissionId: string,
  action: 'accept' | 'reject',
  notes = '',
) {
  return runTournamentMutationOnce(
    `submission:review:${tournamentId}:${submissionId}:${action}`,
    async () => {
      const result = await reviewSubmissionCallable({
        tournamentId,
        submissionId,
        action,
        notes,
      });
      return result.data;
    },
  );
}

export async function calculateTournamentStandings(tournamentId: string) {
  return runTournamentMutationOnce(`results:calculate:${tournamentId}`, async () => {
    const result = await calculateStandingsCallable({ tournamentId });
    return result.data;
  });
}

export async function finalizeTournamentResults(tournamentId: string) {
  return runTournamentMutationOnce(`results:finalize:${tournamentId}`, async () => {
    const result = await finalizeResultsCallable({ tournamentId });
    return result.data;
  });
}

export async function resolveTournamentParticipantDispute(
  tournamentId: string,
  participantUid: string,
  action: 'disqualify' | 'reinstate',
  reason: string,
) {
  return runTournamentMutationOnce(
    `dispute:${tournamentId}:${participantUid}:${action}`,
    async () => {
      const result = await resolveParticipantDisputeCallable({
        tournamentId,
        participantUid,
        action,
        reason,
      });
      return result.data;
    },
  );
}

export async function repairTournamentResults(tournamentId: string) {
  return runTournamentMutationOnce(`results:repair:${tournamentId}`, async () => {
    const result = await repairResultsCallable({ tournamentId });
    return result.data;
  });
}

export async function settleTournamentPayouts(tournamentId: string) {
  return runTournamentMutationOnce(`results:payout:${tournamentId}`, async () => {
    const result = await settlePayoutsCallable({ tournamentId });
    return result.data;
  });
}
